import { Button as HuiButton } from '@headlessui/react'
import type { IconProps } from '@phosphor-icons/react'
import type { ComponentType, ElementType, PropsWithChildren } from 'react'
import { tv, type VariantProps } from 'tailwind-variants'

import { cx } from '../../cx'
import type { ButtonAsProps } from '../../polymorphic-props'

export type ButtonProps = VariantProps<typeof buttonVariants> & {
  IconStart?: ComponentType<IconProps>
  IconEnd?: ComponentType<IconProps>
  classNames?: {
    root?: string
    icon?: string
  }
  growChildren?: 'none' | 'responsive'
}

const buttonVariants = tv({
  base: 'relative inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl text-center transition-colors',
  variants: {
    variant: {
      primary:
        'border-primary-red bg-primary-red hover:layer-bg-hover-dark active:layer-bg-active-dark border-2 text-white',
      secondary:
        'border-primary text-primary hover:layer-bg-hover-light active:layer-bg-active-light border-2 bg-transparent',
      text: 'text-primary hover:layer-bg-hover-light active:layer-bg-active-light border-2 border-transparent bg-transparent',
      chip: '!font-base-copy bg-lightblue hover:layer-bg-hover-light active:layer-bg-active-light !tracking-normal',
      underline:
        '!font-body-small [&_.text]:border-primary !h-auto rounded-none !p-0 leading-6 !tracking-normal [&_.text]:border-b hover:[&_.text]:border-transparent',
    },
    size: {
      small: 'font-button-s h-10 p-4',
      medium: 'font-button-m h-11 px-5 py-4',
      large: 'font-button-l h-12 px-6 py-4',
      responsive: 'font-button h-10 p-4 md:h-11 md:px-5 md:py-4',
    },
    full: { true: '!w-full' },
    width: {
      auto: 'w-auto',
      responsive: 'w-full sm:w-auto',
    },
    onDarkBg: { true: '' },
    transparent: { true: '' },
    active: { true: '' },
    background: {
      white: 'bg-white',
      transparent: 'bg-transparent',
      blue: 'bg-lightblue',
    },
    color: {
      current: 'text-current',
    },
    weight: {
      solid: 'shadow-elevation3',
    },
    disabled: { true: 'pointer-events-none opacity-30' },
    truncate: { true: 'shrink overflow-hidden' },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'medium',
    full: false,
    disabled: false,
    width: 'auto',
  },
  compoundVariants: [
    {
      variant: 'secondary',
      onDarkBg: true,
      className: 'hover:!layer-bg-hover-dark active:!layer-bg-active-dark border-white text-white',
    },
    {
      variant: 'secondary',
      transparent: true,
      className: 'opacity-60 hover:opacity-100',
    },
    {
      variant: 'text',
      onDarkBg: true,
      className: 'hover:!layer-bg-hover-dark active:!layer-bg-active-dark text-white',
    },
    {
      variant: 'chip',
      active: true,
      className: 'bg-primary hover:layer-bg-hover-dark active:layer-bg-active-dark text-white',
    },
  ],
})

const iconSizes = {
  small: 'w-4 h-4',
  medium: 'w-6 h-6',
  large: 'w-8 h-8',
  responsive: 'w-4 h-4 md:w-6 md:h-6',
}

export function Button<TAs extends ElementType>(
  props: PropsWithChildren<ButtonProps> & ButtonAsProps<TAs>
) {
  const {
    ref,
    disabled,
    size = 'responsive',
    classNames,
    IconStart = null,
    IconEnd = null,
    children,
    onDarkBg,
    full,
    active,
    transparent,
    truncate,
    growChildren = 'responsive',
    ...rest
  } = props

  return (
    <BaseButton
      ref={ref}
      className={cx(
        buttonVariants({
          size,
          disabled,
          onDarkBg,
          full,
          active,
          transparent,
          truncate,
          ...rest,
        }),
        classNames?.root
      )}
      disabled={disabled || false}
      aria-disabled={disabled || false}
      {...rest}
    >
      {IconStart && <IconStart className={cx(iconSizes[size], classNames?.icon)} />}
      <span
        className={cx('text self-center', {
          'sm:grow': growChildren === 'responsive',
          truncate,
        })}
      >
        {children}
      </span>
      {IconEnd && <IconEnd className={cx(iconSizes[size], classNames?.icon)} />}
    </BaseButton>
  )
}

export function BaseButton<TAs extends ElementType>(props: ButtonAsProps<TAs>) {
  const { as: Comp = HuiButton, ref, ...rest } = props
  return <Comp {...rest} ref={ref}></Comp>
}
